import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { Box, Chip, Container, Link, Typography } from '@mui/material';
import Hero from '../../sections/Hero';
import JunkRemovalInfo from '../../sections/JunkRemovalInfo';
import FaqAccordion, { faqSchema } from '../../sections/FaqAccordion';
import QuoteRequestForm from '../../sections/QuoteRequestForm';
import NotFoundPage from '../NotFoundPage';
import { getJunkRemovalCity } from './junkRemovalCities';
import { PHONE_DISPLAY, SITE_URL } from '../../../utils/site';

const JunkRemovalCityPage: React.FC = () => {
  const { city: slug } = useParams<{ city: string }>();
  const city = slug ? getJunkRemovalCity(slug) : undefined;

  if (!city) {
    return <NotFoundPage />;
  }

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Service',
        serviceType: 'Junk removal',
        name: `Junk Removal in ${city.name}`,
        url: `${SITE_URL}/junk-removal/${city.slug}`,
        provider: { '@type': 'MovingCompany', name: 'JBR Delivery LLC', telephone: PHONE_DISPLAY, url: SITE_URL },
        areaServed: city.communities.map((name) => ({ '@type': 'Place', name: `${name}, WI` })),
      },
      faqSchema(city.faqs),
    ],
  };

  return (
    <>
      <Helmet>
        <title>{city.title}</title>
        <meta name="description" content={city.description} />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      <Hero
        title={`Junk Removal in ${city.name}`}
        subtitle={city.tagline}
        backgroundImage="/assets/images/junk-removal-bg.jpg"
        defaultService="junk-removal"
      />

      <Box component="section" py={8}>
        <Container maxWidth="lg">
          <Typography variant="h2" component="h2" gutterBottom>
            {city.heading}
          </Typography>
          <Typography variant="body1" paragraph>
            {city.intro}
          </Typography>

          <Typography variant="h6" component="h3" sx={{ mt: 4, mb: 2 }}>
            Areas we cover
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {city.communities.map((name) => (
              <Chip key={name} label={name} variant="outlined" />
            ))}
          </Box>

          {city.sections.map((section) => (
            <Box key={section.heading} mt={6}>
              <Typography variant="h3" component="h2" gutterBottom>
                {section.heading}
              </Typography>
              {section.paragraphs.map((paragraph) => (
                <Typography key={paragraph} variant="body1" paragraph>
                  {paragraph}
                </Typography>
              ))}
            </Box>
          ))}
        </Container>
      </Box>

      <JunkRemovalInfo />

      <Box component="section" py={8}>
        <Container maxWidth="lg">
          <Typography variant="h2" component="h2" gutterBottom align="center" sx={{ mb: 6 }}>
            Junk removal questions
          </Typography>
          <FaqAccordion faqs={city.faqs} />

          <Typography variant="h6" component="h3" sx={{ mt: 6, mb: 1 }}>
            Helpful local links
          </Typography>
          <Box component="ul" sx={{ pl: 3, mt: 0 }}>
            {city.resources.map((resource) => (
              <li key={resource.url}>
                <Link href={resource.url} target="_blank" rel="noopener noreferrer">
                  {resource.label}
                </Link>
              </li>
            ))}
          </Box>

          <Typography variant="body1" sx={{ mt: 4 }}>
            Somewhere else in Northeast Wisconsin? See{' '}
            <Link component={RouterLink} to="/services/junk-removal">
              all our junk removal service areas
            </Link>
            .
          </Typography>
        </Container>
      </Box>

      <QuoteRequestForm defaultService="junk-removal" />
    </>
  );
};

export default JunkRemovalCityPage;
